/** Overview tab — screen state, tape shape, and current conditions.
 * Mock data only. No full-detail tables live here by design.
 */
import { useMemo } from "react";
import {
  CachedBadge, DataNotice, DecisionBadge, EarningsQualityBadge, EmptyNote, KeyRow, MockBadge,
  OptionStateBadge, PanelGrid, RiskLine, ScoreBar, SectionTitle, StatTile, TileRow, WorkspacePanel,
} from "@/workspace/primitives";
import { REGIME_SEMANTICS } from "@/workspace/charts";
import { PriceRegimeStack } from "@/workspace/PriceRegimeChart";
import { IncomeEntryContextCard } from "@/workspace/TechnicalEntryContext";
import { fundamentals } from "@/workspace/mockTickerData";
import { windowRibbonAvailable } from "@/workspace/live/workspaceLiveAdapter";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";
import { useWorkspaceSelection } from "@/workspace/selection";

export function OverviewTab({ t }) {
  const recent = useMemo(() => (t.series || []).slice(-100), [t.series]);
  const ribbonOk = !t.live || windowRibbonAvailable(recent);
  const { decision } = useWorkspaceSelection();
  const f = t.live ? t.fundamentalsLive : fundamentals(t.ticker);
  const risks = [...(t.blockers || []), ...(t.keyRisks || [])];
  const notice = t.live ? <CachedBadge /> : <MockBadge />;
  const pe = t.live ? f?.pe : f?.metrics?.find((m) => m.key === "pe")?.value;
  const fwd = t.live ? f?.forwardPe : f?.metrics?.find((m) => m.key === "forwardPe")?.value;

  return (
    <div className="space-y-3">
      <WorkspacePanel
        title="Decision summary"
        notice={notice}
        hint={t.live ? "Factual data, event and parameter notes from current cached GETs." : "Mock composition of quote fields, earnings window and configured screen parameters."}
        actions={<DecisionBadge state={decision.state} text={decision.headline} title={decision.detail} wrap testid="overview-decision-badge" />}
        testid="overview-decision"
      >
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
          {t.decisionSummary}
        </p>
        <p className="text-[11px] mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
          Selected {decision.dte}-DTE expiry: {decision.detail}
        </p>
        <p className="text-[10px] mt-2 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="overview-screen-helper">
          This summary reflects available data and configured MarkovLab screen parameters. It is not investment advice or a recommendation.
        </p>
        <p className="text-[10px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="overview-colour-helper">
          Blue/grey is informational or observed. Amber is a data, event or parameter flag. Red is an unavailable field or unavailable product action. Green is only an observed or completed data state.
        </p>
      </WorkspacePanel>

      <WorkspacePanel
        title="Price, regime and trend"
        subtitle="Last 100 sessions · confirmed ribbon and trend tape share the candle date axis"
        notice={notice}
        hint={t.live ? "Cached OHLCV. Regime ribbon only when every displayed session date matches regime history exactly." : "Mock OHLCV. The confirmed ribbon is a fixture majority, not a live HMM run. Instant regime is in the tooltip and header."}
        testid="overview-price"
      >
        {recent.length ? (
          <PriceRegimeStack
            series={recent}
            height={180}
            showVolume
            showSma={!t.live || (t.hasPerBarSma50 || t.hasPerBarSma200)}
            smaKeys={t.live ? t.smaKeys : undefined}
            ribbonAvailable={ribbonOk}
            alignmentMessage={ribbonOk ? null : (t.alignmentMessage || LIVE_UNAVAILABLE.regimeAlignment)}
            showTrendTape={!t.live || t.hasTrendTape}
            live={Boolean(t.live)}
            testid="overview-price-chart"
          />
        ) : (
          <EmptyNote>OHLCV unavailable from current cached data.</EmptyNote>
        )}
      </WorkspacePanel>

      {t.live ? (
        <WorkspacePanel title="Income entry context" notice={<CachedBadge />} testid="income-entry-context">
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.emaDaily}</p>
        </WorkspacePanel>
      ) : (
        <IncomeEntryContextCard ticker={t} />
      )}

      <TileRow>
        <StatTile
          label="Regime"
          value={t.regime.state}
          sub={t.regime.confidence != null
            ? `${Math.round(t.regime.confidence * 100)}% confidence · held ${t.regime.ageBars ?? "—"} bars`
            : "confidence unavailable"}
          testid="overview-tile-regime"
        />
        <StatTile
          label="Technical score"
          value={t.technicalScore != null ? t.technicalScore.toFixed(1) : "—"}
          sub="of 10 · inputs in the Technicals tab"
          testid="overview-tile-technical"
        />
        <StatTile
          label="Fundamental score"
          value={t.fundamentalScore != null ? t.fundamentalScore.toFixed(1) : "—"}
          sub={pe != null || fwd != null ? `P/E ${pe ?? "—"} · fwd ${fwd ?? "—"}` : LIVE_UNAVAILABLE.notInCache}
          testid="overview-tile-fundamental"
        />
        <StatTile
          label={t.live && t.earnings?.past ? "Most recent earnings" : "Next earnings"}
          value={t.live && t.earnings?.past
            ? (t.earnings.date || "Date unavailable")
            : (t.earnings.daysTo != null ? `${t.earnings.daysTo}d` : "—")}
          sub={t.live && t.earnings?.past
            ? "Past date supplied — next event unavailable"
            : (t.earnings.date || "Date unavailable")}
          badge={t.live && t.earnings?.past
            ? null
            : <EarningsQualityBadge quality={t.earnings.quality} />}
          testid="overview-tile-earnings"
        />
        <StatTile
          label="Options quality"
          value={t.quoteQualityLabel}
          badge={<OptionStateBadge state={t.optionsState} />}
          testid="overview-tile-options"
        />
      </TileRow>

      <PanelGrid>
        <WorkspacePanel title="Data, event and parameter notes" testid="overview-risks">
          {risks.length ? (
            <div className="divide-y" style={{ borderColor: "var(--hoip-divider)" }}>
              {risks.map((r) => <RiskLine key={r.text} level={r.level} text={r.text} />)}
            </div>
          ) : (
            <EmptyNote>No current data, event or parameter notes for this ticker.</EmptyNote>
          )}
          <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
            <DataNotice adminText="Condition list is a mock composition. In production it is assembled from option_readiness, wing_scenarios.validation_errors and the confirmed-regime gate." />
          </div>
        </WorkspacePanel>

        <WorkspacePanel
          title="What the regime means"
          subtitle={t.regime.state}
          testid="overview-regime-meaning"
        >
          <p className="text-[11px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
            {REGIME_SEMANTICS[t.regime.state] || "No description for this state."}
          </p>
          <div className="mt-2 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
            <KeyRow label="Instant state">{t.regime.instantState}</KeyRow>
            <KeyRow label="Confirmed (5 sessions)">{t.regime.confirmedState}</KeyRow>
            <KeyRow label="Sojourn so far">{t.regime.sojournDays != null ? `${t.regime.sojournDays}d` : "—"}</KeyRow>
            <KeyRow label="P75 sojourn">{t.regime.sojournP75 != null ? `${t.regime.sojournP75}d` : "—"}</KeyRow>
          </div>
          <div className="mt-2">
            <SectionTitle>Score summary</SectionTitle>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-20 shrink-0" style={{ color: "var(--hoip-text-secondary)" }}>Technical</span>
                <ScoreBar value={t.technicalScore} />
              </div>
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-20 shrink-0" style={{ color: "var(--hoip-text-secondary)" }}>Fundamental</span>
                <ScoreBar value={t.fundamentalScore} />
              </div>
            </div>
          </div>
        </WorkspacePanel>
      </PanelGrid>
    </div>
  );
}
